<template>
  <div
    v-if="actionsForSelect.length > 0"
    :class="width === 'full' ? 'w-full' : 'shrink-0'"
  >
    <Dropdown
      :placement="isRtl ? 'bottom-end' : 'bottom-start'"
      :should-close-on-blur="false"
    >
      <Button
        variant="ghost"
        trailing-icon="chevron-down"
        padding="tight"
        class="panel-toolbar-action-trigger"
        :class="{
          'w-full justify-between': width === 'full',
          'min-w-[8.5rem]': width === 'auto',
        }"
        dusk="action-select"
        :aria-label="__('Select Action')"
      >
        {{ __('Actions') }}
      </Button>

      <template #menu>
        <DropdownMenu width="240" class="panel-row-action-menu panel-toolbar-action-menu">
          <div class="panel-row-action-menu-items">
            <DropdownMenuItem
              v-for="action in regularActions"
              :key="action.value"
              as="button"
              size="large"
              :disabled="action.disabled"
              @click="handleActionSelect(action.value)"
            >
              {{ action.label }}
            </DropdownMenuItem>

            <template v-if="pivotActionsList.length > 0">
              <DropdownMenuHeading v-if="pivotName">
                {{ pivotName }}
              </DropdownMenuHeading>

              <DropdownMenuItem
                v-for="action in pivotActionsList"
                :key="action.value"
                as="button"
                size="large"
                :disabled="action.disabled"
                @click="handleActionSelect(action.value)"
              >
                {{ action.label }}
              </DropdownMenuItem>
            </template>
          </div>
        </DropdownMenu>
      </template>
    </Dropdown>

    <component
      class="text-left"
      v-if="actionModalVisible"
      :show="actionModalVisible"
      :is="selectedAction?.component"
      :working="working"
      :selected-resources="selectedResources"
      :resource-name="resourceName"
      :action="selectedAction"
      :errors="errors"
      @confirm="executeAction"
      @close="closeConfirmationModal"
    />

    <component
      v-if="responseModalVisible"
      :show="responseModalVisible"
      :is="actionResponseData?.modal"
      @confirm="closeResponseModal"
      @close="closeResponseModal"
      :data="actionResponseData"
    />
  </div>
</template>

<script setup>
import { useActions } from '@/composables/useActions'
import { useStore } from 'vuex'
import { computed, onMounted, onUnmounted } from 'vue'
import { Button } from 'laravel-nova-ui'
import DropdownMenuHeading from './Dropdowns/DropdownMenuHeading.vue'

const store = useStore()
const emitter = defineEmits(['actionExecuted'])

const props = defineProps({
  width: { type: String, default: 'auto' },
  pivotName: { type: String, default: null },
  resourceName: {},
  viaResource: {},
  viaResourceId: {},
  viaRelationship: {},
  relationshipType: {},
  pivotActions: {
    type: Object,
    default: () => ({ name: 'Pivot', actions: [] }),
  },
  actions: { type: Array, default: [] },
  selectedResources: { type: [Array, String], default: () => [] },
  endpoint: { type: String, default: null },
  triggerDuskAttribute: { type: String, default: null },
})

const isRtl = computed(() => Nova.config('rtlEnabled'))

const {
  errors,
  actionModalVisible,
  responseModalVisible,
  closeConfirmationModal,
  closeResponseModal,
  handleActionClick,
  selectedAction,
  setSelectedActionKey,
  determineActionStrategy,
  working,
  executeAction,
  availableActions,
  availablePivotActions,
  actionResponseData,
} = useActions(props, emitter, store)

const actionsForSelect = computed(() => [
  ...availableActions.value.map(a => ({
    value: a.uriKey,
    label: a.name,
    disabled: a.authorizedToRun === false,
    group: null,
  })),
  ...availablePivotActions.value.map(a => ({
    value: a.uriKey,
    label: a.name,
    disabled: a.authorizedToRun === false,
    group: props.pivotName,
  })),
])

const regularActions = computed(() =>
  actionsForSelect.value.filter(action => !action.group)
)

const pivotActionsList = computed(() =>
  actionsForSelect.value.filter(action => action.group)
)

const handleActionSelect = uriKey => {
  handleActionClick(uriKey)
}

const findActionData = key => {
  return actionsForSelect.value.find(action => action.value === key) ?? null
}

onMounted(() => {
  Nova.$on('open-action-model', key => {
    const data = findActionData(key)

    if (data && !data.disabled) {
      handleActionSelect(key)
    }
  })
})

onUnmounted(() => {
  Nova.$off('open-action-model')
})
</script>
